import { DOMAIN_STATUS } from '../settings/domains/constants';
import {
    getMarketingSenderIdFromRow,
    getMarketingSenderRowFromApiResponse,
    getMarketingSendersRawList,
    mergeEnsureSendersIntoSelectOptions,
    normalizeMarketingSendersResponse,
} from './marketingSenderOptions';

export function hostFromEmail(email) {
    if (typeof email !== 'string') {
        return null;
    }
    const s = email.trim().toLowerCase();
    const i = s.lastIndexOf('@');
    return i > 0 && i < s.length - 1 ? s.slice(i + 1) : null;
}

export function getVerifiedHostsFromDomains(domains) {
    const list = Array.isArray(domains) ? domains : [];
    return list
        .filter((d) => d?.status === DOMAIN_STATUS.RUNNING && d?.domain)
        .map((d) => String(d.domain).trim().toLowerCase());
}

export function isSenderDomainVerified(sender, verifiedHosts) {
    const host = hostFromEmail(sender.senderEmail);
    return host != null && verifiedHosts.includes(host);
}

/**
 * Keeps only senders whose email domain matches a verified (RUNNING) provider domain.
 * Same rule as marketing settings senders table (`domainVerified`).
 */
export function filterMarketingSendersByVerifiedDomain(rawSenders, domains) {
    const verifiedHosts = getVerifiedHostsFromDomains(domains);
    const list = Array.isArray(rawSenders) ? rawSenders : [];
    return list.filter((s) => s && isSenderDomainVerified(s, verifiedHosts));
}

function getFulfilledArray(result) {
    if (result?.status !== 'fulfilled' || result.value === undefined || result.value === null) {
        return [];
    }
    if (Array.isArray(result.value)) {
        return result.value;
    }
    return [];
}

/**
 * Parses `Promise.allSettled([listSenders(), listDomains()])` results into normalized inputs.
 * Shared by marketing settings senders and campaign sender Select.
 *
 * @param {PromiseSettledResult<unknown>} sendersResult
 * @param {PromiseSettledResult<unknown>} domainsResult
 * @returns {{ rawSenders: object[], domains: unknown[] }}
 */
export function getMarketingSendersAndDomainsFromSettled(sendersResult, domainsResult) {
    const sendersData = sendersResult?.status === 'fulfilled' ? sendersResult.value : null;
    const rawSenders = getMarketingSendersRawList(sendersData);
    const domains = getFulfilledArray(domainsResult);
    return { rawSenders, domains };
}

/**
 * All sender rows with `domainVerified` (settings senders table).
 *
 * @param {object[]} rawSenders
 * @param {unknown[]} domains
 * @returns {object[]}
 */
export function enrichSendersWithDomainVerification(rawSenders, domains) {
    const verifiedHosts = getVerifiedHostsFromDomains(domains);
    const list = Array.isArray(rawSenders) ? rawSenders : [];
    return list.map((sender) => ({
        ...sender,
        domainVerified: isSenderDomainVerified(sender, verifiedHosts),
    }));
}

/**
 * Campaign form Select options: verified domains, plus optional rows kept selectable
 * (campaign sender, newly created sender).
 *
 * @param {PromiseSettledResult<unknown>} sendersResult
 * @param {PromiseSettledResult<unknown>} domainsResult
 * @param {Array<{ row?: object, id?: string|number, label?: string, subLabel?: string }>} [ensureEntries]
 */
export function buildMarketingSenderSelectOptions(
    sendersResult,
    domainsResult,
    ensureEntries = [],
) {
    const { rawSenders, domains } = getMarketingSendersAndDomainsFromSettled(
        sendersResult,
        domainsResult,
    );
    const verified = normalizeMarketingSendersResponse(
        filterMarketingSendersByVerifiedDomain(rawSenders, domains),
    );

    const entries = (Array.isArray(ensureEntries) ? ensureEntries : []).map((entry) => {
        const id = getMarketingSenderIdFromRow(entry?.row) ?? entry?.id;
        if (id == null || id === '') {
            return entry;
        }
        return {
            ...entry,
            id,
            row:
                getMarketingSenderRowFromApiResponse(entry?.row) ??
                rawSenders.find((sender) => String(sender.id ?? sender.senderId) === String(id)) ??
                undefined,
        };
    });

    return mergeEnsureSendersIntoSelectOptions(verified, entries);
}

/** @deprecated Use buildMarketingSenderSelectOptions */
export function buildVerifiedMarketingSenderSelectOptions(sendersResult, domainsResult) {
    return buildMarketingSenderSelectOptions(sendersResult, domainsResult);
}
